import React, { useEffect, useState, useCallback } from "react";
import api, { API, apiError, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PriceTag } from "@/components/PriceTag";
import { Briefcase, Plus, Pencil, Trash2, Copy, MessageCircle, MapPin, Building2, FileText } from "lucide-react";
import { JOB_PHOTOS, jobPhotoSrc } from "@/lib/jobPhotos";

const EMPTY = { post: "", company: "", country: "", service_charge: "", service_charge_mrp: "", salary: "", vacancies: "", description: "", photo: "" };
const STATUS = ["applied", "shortlisted", "selected", "rejected"];
const STATUS_CLS = { applied: "border-blue-400/40 text-blue-300", shortlisted: "border-amber-400/40 text-amber-300", selected: "border-emerald-400/40 text-emerald-300", rejected: "border-destructive/40 text-destructive" };

export default function JobsPanel() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const token = localStorage.getItem("token");
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [ref, setRef] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const [j, a] = await Promise.all([api.get("/jobs"), api.get("/job-applications")]);
    setJobs(j.data); setApps(a.data);
  }, []);
  useEffect(() => {
    load();
    api.get("/my-referral").then((r) => setRef(r.data.referral_code)).catch(() => {});
  }, [load]);

  const shareLink = (j) => `${window.location.origin}/jobs?job=${j.id}${ref ? `&ref=${ref}` : ""}`;
  const waLink = (j) => `https://wa.me/?text=${encodeURIComponent(`Job opening: ${j.post} at ${j.company}, ${j.country}. Apply here: ${shareLink(j)}`)}`;

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (j) => { setEditId(j.id); setForm({ ...EMPTY, ...j, service_charge: String(j.service_charge ?? ""), vacancies: String(j.vacancies || "") }); setOpen(true); };

  const save = async () => {
    if (!form.post || !form.company || !form.country) { toast.error("Post, company and country are required"); return; }
    const body = { ...form, service_charge: Number(form.service_charge) || 0, service_charge_mrp: Number(form.service_charge_mrp) || 0, vacancies: Number(form.vacancies) || 0 };
    try {
      if (editId) await api.put(`/jobs/${editId}`, body); else await api.post("/jobs", body);
      toast.success(editId ? "Job updated" : "Job posted"); setOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/jobs/${id}`); toast.success("Job deleted"); load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => {
    try { await api.put(`/job-applications/${id}/status`, { status }); load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Employment" title="Jobs & Employment" subtitle="Post overseas jobs, share them, and review candidate applications"
        action={isOwner && <Button data-testid="add-job-btn" onClick={openNew} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2"><Plus className="w-4 h-4" /> Post Job</Button>} />
      <div className="p-4 sm:p-8 space-y-5">
        <div className="flex gap-2">
          {[["jobs", `Jobs (${jobs.length})`], ["apps", `Applications (${apps.length})`]].map(([k, l]) => (
            <Button key={k} size="sm" data-testid={`jobs-tab-${k}`} variant={tab === k ? "default" : "outline"} onClick={() => setTab(k)}
              className={tab === k ? "bg-gold text-[#0a2e22] hover:bg-gold/90" : "border-gold/40 text-gold"}>{l}</Button>
          ))}
        </div>

        {tab === "jobs" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.length === 0 && <p className="text-sm text-muted-foreground col-span-full text-center py-10">No jobs posted yet.</p>}
            {jobs.map((j) => (
              <Card key={j.id} data-testid={`job-card-${j.id}`} className="overflow-hidden bg-card/40 border-gold/20 flex flex-col">
                <img src={jobPhotoSrc(j.photo)} alt={j.post} className="h-36 w-full object-cover" />
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-serif font-semibold text-white">{j.post}</p>
                    {isOwner && (
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" data-testid={`edit-job-${j.id}`} onClick={() => openEdit(j)} className="h-7 w-7 text-gold"><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" data-testid={`delete-job-${j.id}`} onClick={() => remove(j.id)} className="h-7 w-7 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Building2 className="w-3 h-3 text-gold" /> {j.company}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3 text-gold" /> {j.country}</p>
                  {j.salary && <p className="text-xs text-emerald-200 mt-1">Salary: {j.salary}</p>}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                    <div><p className="text-[10px] text-muted-foreground uppercase">Service charge</p><p className="text-gold font-mono"><PriceTag price={j.service_charge} mrp={j.service_charge_mrp} /></p></div>
                    <Badge variant="outline" className="border-gold/40 text-gold text-[10px]">{j.applications} applied</Badge>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" data-testid={`copy-job-${j.id}`} onClick={() => { navigator.clipboard.writeText(shareLink(j)); toast.success("Job link copied"); }} className="flex-1 bg-gold text-[#0a2e22] hover:bg-gold/90 text-[11px] h-8 gap-1"><Copy className="w-3 h-3" /> Copy link</Button>
                    <a data-testid={`wa-job-${j.id}`} href={waLink(j)} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-md bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/40 hover:bg-[#25D366]/25 text-[11px] font-medium"><MessageCircle className="w-3 h-3" /> WhatsApp</a>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {tab === "apps" && (
          <Card className="p-4 bg-card/40 border-gold/20 overflow-x-auto" data-testid="applications-list">
            {apps.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No applications yet.</p>}
            {apps.map((a) => (
              <div key={a.id} data-testid={`application-${a.id}`} className="flex flex-wrap items-center justify-between gap-3 py-3 border-b border-border/40 last:border-0">
                <div className="min-w-0">
                  <p className="text-sm text-white font-medium">{a.candidate_name} <span className="text-muted-foreground font-mono text-xs">· {a.candidate_phone}</span></p>
                  <p className="text-xs text-muted-foreground">{a.job_post} — {a.job_company}, {a.job_country}{a.referrer_name ? ` · via ${a.referrer_name}` : ""}</p>
                  {a.note && <p className="text-[11px] text-muted-foreground/80 mt-0.5">"{a.note}"</p>}
                </div>
                <div className="flex items-center gap-2">
                  <a href={`${API}/job-applications/${a.id}/file/cv?auth=${token}`} target="_blank" rel="noopener noreferrer" data-testid={`app-cv-${a.id}`} className="text-[11px] text-gold underline flex items-center gap-1"><FileText className="w-3 h-3" /> CV</a>
                  <a href={`${API}/job-applications/${a.id}/file/passport?auth=${token}`} target="_blank" rel="noopener noreferrer" data-testid={`app-passport-${a.id}`} className="text-[11px] text-gold underline flex items-center gap-1"><FileText className="w-3 h-3" /> Passport</a>
                  {isOwner ? (
                    <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                      <SelectTrigger data-testid={`app-status-${a.id}`} className="h-8 w-32 text-xs bg-card/60 capitalize"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#05261D] border-gold/20">{STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <Badge variant="outline" className={`capitalize ${STATUS_CLS[a.status]}`}>{a.status}</Badge>}
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#05261D] border-gold/20 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-gold flex items-center gap-2"><Briefcase className="w-5 h-5" /> {editId ? "Edit Job" : "Post a Job"}</DialogTitle>
            <DialogDescription>Fill the job details and pick a photo.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            {[["post", "Post / position", "e.g. Heavy Driver"], ["company", "Company", "e.g. Al Futtaim Group"], ["country", "Country", "e.g. Saudi Arabia"],
              ["service_charge", "Service charge — offer (₹)", "e.g. 45000"], ["service_charge_mrp", "Full charge (₹, shown crossed out)", "optional"], ["salary", "Salary (optional)", "e.g. 1800 SAR / month"], ["vacancies", "Vacancies (optional)", "e.g. 20"]].map(([k, l, ph]) => (
              <div key={k}><Label className="text-xs text-muted-foreground">{l}</Label>
                <Input data-testid={`job-${k}-input`} type={["service_charge", "service_charge_mrp", "vacancies"].includes(k) ? "number" : "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-card/60" placeholder={ph} /></div>
            ))}
            <div className="sm:col-span-2"><Label className="text-xs text-muted-foreground">Description (optional)</Label>
              <Textarea data-testid="job-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-card/60 min-h-[60px]" placeholder="Duty hours, accommodation, food, visa…" /></div>
          </div>
          <Label className="text-xs text-muted-foreground mt-2">Job photo — pick one of 30</Label>
          <div className="grid grid-cols-5 sm:grid-cols-6 gap-1.5" data-testid="job-photo-grid">
            {JOB_PHOTOS.map((p) => (
              <button key={p.key} type="button" data-testid={`job-photo-${p.key}`} onClick={() => setForm({ ...form, photo: p.key })} title={p.label}
                className={`relative rounded-md overflow-hidden border-2 transition-colors ${form.photo === p.key ? "border-gold" : "border-transparent hover:border-gold/40"}`}>
                <img src={p.src} alt={p.label} loading="lazy" className="w-full aspect-square object-cover" />
                <span className="absolute bottom-0 inset-x-0 bg-black/60 text-[8px] text-white px-0.5 truncate">{p.label}</span>
              </button>
            ))}
          </div>
          <DialogFooter><Button data-testid="save-job-btn" onClick={save} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">Save Job</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
